/**
 * What the staff child form posts.
 *
 * A child update writes every key it is sent, and null means "clear it".
 * So when editing, a field is only sent if the record the form opened from
 * actually carried it: leaving the key out is the only signal for
 * "unchanged". This form once opened from a record with no medical fields
 * and every save wrote "no medical conditions" over the real ones.
 */

const has = (record, key) =>
  Boolean(record) && Object.prototype.hasOwnProperty.call(record, key);

const firstContact = (record) => record?.emergency_contacts?.[0] || {};

const contactChanged = (form, record) => {
  const original = firstContact(record);
  return (
    form.emergency_name !== (original.name || "") ||
    form.emergency_phone !== (original.phone || "") ||
    form.emergency_relation !==
      (original.relation || original.relationship || "")
  );
};

export function buildChildPayload(form, { mode, initialData, parentId } = {}) {
  const editing = mode === "edit";
  // When creating there is nothing to preserve, so everything is sent.
  const loaded = (key) => !editing || has(initialData, key);

  const payload = {
    first_name: form.first_name,
    last_name: form.last_name,
    date_of_birth: form.date_of_birth,
    grade: form.grade,
    jersey_size: form.jersey_size.toLowerCase(),
  };

  if (loaded("medical_conditions")) {
    payload.medical_conditions = form.medical_conditions || null;
    payload.has_no_medical_conditions = !form.medical_conditions;
  }

  if (loaded("health_insurance_number")) {
    payload.health_insurance_number = form.health_insurance_number || null;
  }

  if (loaded("after_school_attendance")) {
    payload.after_school_attendance = form.after_school_attendance;
    // Keep the program the family named; the form has no field for it.
    payload.after_school_program = form.after_school_attendance
      ? initialData?.after_school_program || "Default Program"
      : null;
  }

  // The API replaces the whole contact list with what it is sent, and this
  // form shows only the first contact.
  if (!editing || contactChanged(form, initialData)) {
    payload.emergency_contacts = [
      {
        name: form.emergency_name,
        relation: form.emergency_relation || "Other",
        phone: form.emergency_phone,
        is_primary: true,
      },
    ];
  }

  if (mode === "create" && parentId) {
    payload.parent_id = parentId;
  }

  return payload;
}
